import { useCapabilityRegistryContext } from '@mobile-mapping/providers';
import type {
  CheckboxMenuItem,
  ControlMenuItemType,
} from '@trimble/ui-components/solidjs';
import { t } from '@trimble/i18n';
import { asError, getLogger } from '@trimble/logging';
import {
  centerSnapType,
  faceSnapType,
  lineSnapType,
  pointSnapType,
  SnappingCapability,
} from '@trimble/web3d-capabilities';
import type { SnapType } from '@trimble/web3d-providers';
import { Accessor, createEffect, createMemo, createSignal } from 'solid-js';

export const MAIN_TOOLBAR_SNAPPING_STORAGE_KEY =
  'main-toolbar-snapping-last-tool';

export interface UseMainToolbarSnappingResult {
  readonly snappingMenuItems: Accessor<ControlMenuItemType[]>;
}

interface SnapMenuItemDefinition {
  readonly id: string;
  readonly labelKey: string;
  readonly icon: string;
  readonly snapTypes: readonly SnapType[];
}

// Mirrors the snap groups in BasicSnapping.tsx.
const SNAP_MENU_ITEMS: readonly SnapMenuItemDefinition[] = [
  {
    id: 'face-snap',
    labelKey: 'web3D.toolbar.actions.faceSnapType',
    icon: 'tc-icon-snap-face',
    snapTypes: faceSnapType,
  },
  {
    id: 'line-snap',
    labelKey: 'web3D.toolbar.actions.lineSnapType',
    icon: 'tc-icon-snap-line',
    snapTypes: lineSnapType,
  },
  {
    id: 'point-snap',
    labelKey: 'web3D.toolbar.actions.pointSnapType',
    icon: 'tc-icon-snap-endpoint',
    snapTypes: pointSnapType,
  },
  {
    id: 'center-snap',
    labelKey: 'web3D.toolbar.actions.centerSnapType',
    icon: 'tc-icon-snap-center',
    snapTypes: centerSnapType,
  },
];

const isSnapTypeActive = (
  activeSnapTypes: readonly SnapType[],
  snapType: SnapType
): boolean =>
  activeSnapTypes.some((activeSnapType) => activeSnapType === snapType);

const isSnapTypeGroupActive = (
  activeSnapTypes: readonly SnapType[],
  snapTypes: readonly SnapType[]
): boolean =>
  snapTypes.every((snapType) => isSnapTypeActive(activeSnapTypes, snapType));

/**
 * The last active snap group cannot be turned off, so that at least one snap
 * type stays enabled while the dropdown is in use. That item is disabled.
 */
const isLastActiveSnapMenuItem = (
  activeSnapTypes: readonly SnapType[],
  snapTypes: readonly SnapType[]
): boolean => {
  if (!isSnapTypeGroupActive(activeSnapTypes, snapTypes)) {
    return false;
  }

  const activeGroupCount = SNAP_MENU_ITEMS.filter((definition) =>
    isSnapTypeGroupActive(activeSnapTypes, definition.snapTypes)
  ).length;

  return activeGroupCount === 1;
};

const readStoredSnapMenuItemIds = (): string[] => {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(MAIN_TOOLBAR_SNAPPING_STORAGE_KEY);
  } catch (error) {
    getLogger().warn(
      'Failed to read main toolbar snapping stored selection',
      { storageKey: MAIN_TOOLBAR_SNAPPING_STORAGE_KEY },
      asError(error)
    );
    return [];
  }

  if (!raw) {
    return [];
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.filter((id): id is string => typeof id === 'string');
    }
  } catch {
    // Legacy single-id value stored before multiple-selection support.
  }

  return [raw];
};

const snapMenuItemIdsToSnapTypes = (itemIds: readonly string[]): SnapType[] =>
  itemIds.flatMap((itemId) => {
    const definition = SNAP_MENU_ITEMS.find((item) => item.id === itemId);
    return definition ? [...definition.snapTypes] : [];
  });

/**
 * @intent Owns main-toolbar snapping dropdown state, mirroring the snap toggles
 * previously exposed in the debug panel.
 */
export const useMainToolbarSnapping = (): UseMainToolbarSnappingResult => {
  const { capabilityRegistry } = useCapabilityRegistryContext();
  const snappingCapability = capabilityRegistry.get(SnappingCapability);
  const [hasRestoredFromStorage, setHasRestoredFromStorage] =
    createSignal(false);

  createEffect(() => {
    const capability = snappingCapability();
    if (!capability || hasRestoredFromStorage()) {
      return;
    }

    const storedItemIds = readStoredSnapMenuItemIds();
    const snapTypes = snapMenuItemIdsToSnapTypes(storedItemIds);
    if (snapTypes.length > 0) {
      capability.setSnapTypes(snapTypes);
    }

    setHasRestoredFromStorage(true);
  });

  const snappingMenuItems = createMemo((): ControlMenuItemType[] => {
    const capability = snappingCapability();
    if (!capability) {
      return [];
    }

    const activeSnapTypes = capability.snapTypes();
    return SNAP_MENU_ITEMS.map((definition): ControlMenuItemType => {
      const item: CheckboxMenuItem = {
        id: definition.id,
        type: 'checkbox' as const,
        label: t(definition.labelKey),
        icon: definition.icon,
        value: () =>
          isSnapTypeGroupActive(capability.snapTypes(), definition.snapTypes),
        setValue: (enabled: boolean): void => {
          capability.toggleSnapType(enabled, [...definition.snapTypes]);
        },
        disabled: isLastActiveSnapMenuItem(
          activeSnapTypes,
          definition.snapTypes
        ),
      };

      return item;
    });
  });

  return {
    snappingMenuItems,
  };
};
